/**
 *
 * The ChunkLoadError occurs when the browser encounters an error in fetching some JavaScript files (chunks),
 * which were dynamically imported. When we release a new version of our app the previous chunks are deleted and replaced by new ones.
 * If the user has left a browser tab open while we released a new version, and he/she tries to navigate from route A to route B,
 * the browser will try to fetch the chunk that corresponds to that route. Since the file for route B does not exist anymore it
 * will produce a ChunkLoadError error. The user then needs to reload the browser tab in order to get the new version's JavaScript files.
 *
 * What we want to achieve is to provide the user with a seamless navigation experience while also reloading the page
 * so that the user's browser loads the new version's JavaScript files.
 *
 * How we handle it:
 *
 * - We store the route that the user tries to navigate to in every navigation (using the router's `beforeEach` hook).
 * - When we encounter the ChunkLoadError, we redirect the user to the new route using window.location.href,
 *   so this **reloads** the page. We also store a value in `localStorage` that indicates that a reload has taken place.
 *   We need to do that in order to ensure that this redirect happens only **once**.
 * - We clear the `localStorage` value in every successful navigation using the router's `afterEach` hook.
 *
 */
import { type Router } from 'vue-router';

const STORAGE_KEY = 'pageForceRefreshed';

const pageHasAlreadyBeenRefreshed = () => window.localStorage.getItem(STORAGE_KEY) === 'true';

export const handleChunkLoadError = (router: Router) => {
  const chunkLoadErrors = [];

  // Vite emits an event when a chunk fails to load
  // https://github.com/vitejs/vite/pull/12084
  window.addEventListener('vite:preloadError', () => {
    chunkLoadErrors.push(Date.now());
  });

  // Before navigation, make sure chunkLoadErrors array is empty
  router.beforeEach(() => {
    chunkLoadErrors.length = 0;
  });

  // After successful navigation, clear the localStorage value
  router.afterEach(() => {
    window.localStorage.setItem(STORAGE_KEY, 'false');
  });

  router.onError((_, to) => {
    // If after reloading the first time we have a chunk that is missing again we want to prevent an infinite reload loop
    if (chunkLoadErrors.length && !pageHasAlreadyBeenRefreshed()) {
      window.localStorage.setItem(STORAGE_KEY, 'true');
      console.info('Application reload...');
      if (to?.fullPath) {
        const location = `${window.location.origin}/v2${to.fullPath}`;
        window.location.href = location;
      } else {
        window.location.reload();
      }
    }
  });
};
